
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { useToast } from '@/hooks/use-toast';
import { generateTripCode } from '@/utils/tripUtils';
import { tripFormSchema, TripFormValues } from './TripFormSchema';

export const useTripForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<TripFormValues>({
    resolver: zodResolver(tripFormSchema),
    defaultValues: {
      name: '',
      destination: '',
      description: '',
    },
  });

  const onSubmit = async (values: TripFormValues) => {
    if (!user) {
      toast({
        title: "Authentication Error",
        description: "You must be logged in to create a trip.",
        variant: "destructive",
      });
      navigate('/auth');
      return;
    }

    try {
      console.log("Creating trip with values:", values);
      
      // Check if trip name already exists for this user
      const { data: existingTrips, error: checkError } = await supabase
        .from('trips')
        .select('id')
        .eq('owner_id', user.id)
        .eq('name', values.name);

      if (checkError) throw checkError;

      if (existingTrips && existingTrips.length > 0) {
        toast({
          title: "Trip Name Already Exists",
          description: "Please choose a different name for your trip.",
          variant: "destructive",
        });
        return;
      }

      // Generate a unique trip code and invite code
      const tripCode = generateTripCode();
      const inviteCode = generateTripCode();

      // Insert new trip into database with the correct columns
      const { data: trip, error } = await supabase
        .from('trips')
        .insert({
          name: values.name,
          start_date: format(values.startDate, 'yyyy-MM-dd'),
          end_date: format(values.endDate, 'yyyy-MM-dd'),
          destination: values.destination,
          description: values.description || null,
          code: tripCode,
          invite_code: inviteCode,
          owner_id: user.id,
        })
        .select()
        .single();

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      console.log("Trip created successfully:", trip);

      // Add owner as a trip member
      const { error: memberError } = await supabase
        .from('trip_members')
        .insert({
          trip_id: trip.id,
          user_id: user.id,
        });

      if (memberError) {
        console.error("Error adding trip member:", memberError);
      }

      // Show success toast
      toast({
        title: "Trip Created Successfully!",
        description: `Your trip code is: ${tripCode}`,
      });

      // Redirect to trip layout page instead of standalone itinerary
      navigate(`/trips/${trip.id}/itinerary`);
    } catch (error) {
      console.error('Error creating trip:', error);
      toast({
        title: "Failed to Create Trip",
        description: "There was an error creating your trip. Please try again.",
        variant: "destructive",
      });
    }
  };

  return { form, onSubmit };
};
